



import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowPathIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import TableComponent from "../../components/TableComponent";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import SponsorModal from "../../components/admin/SponsorModal";
import {
  deleteSponsor,
  fetchSponsors,
} from "../../features/admin/SponsorSlice";
import { toast } from "react-toastify";
import AuditHistoryButton, {
  canViewAuditHistory,
} from "../../components/admin/AuditHistoryButton";

const getMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_API_URL}media/${url}`;
};

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((permission) => permission.permissionSlug)
    : [];
};

const hasPermission = (requiredPermission) => {
  const userId = localStorage.getItem("userId");
  if (userId == 1) {
    return true;
  }

  return getUserPermissions().includes(requiredPermission);
};

const Sponsors = () => {
  const dispatch = useDispatch();
  const { sponsors, status, error } = useSelector((state) => state.sponsors);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentSponsor, setCurrentSponsor] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchSponsors());
    }
  }, [status, dispatch]);

  const filteredSponsors = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();
    if (!keyword) return sponsors || [];

    return (sponsors || []).filter((item) => {
      return [
        item.name,
        item.businessField,
        item.phoneNumber,
        item.email,
        item.website,
        item.address,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword));
    });
  }, [sponsors, searchValue]);

  const handleOpenModal = (item = null) => {
    setCurrentSponsor(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentSponsor(null);
  };

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteId(null);
    setIsDeleteOpen(false);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteSponsor(deleteId)).unwrap();
      toast.success("Xóa nhà tài trợ thành công!");
      handleCloseDeleteModal();
    } catch (error) {
      toast.error(error?.messages || "Đã xảy ra lỗi khi xóa nhà tài trợ!");
    }
  };

  const headers = [
    {
      key: "logo",
      label: "Logo",
      render: (item) => (
        <div className="w-16 h-16 rounded border bg-base-100 flex items-center justify-center overflow-hidden">
          {item.logo ? (
            <img
              src={getMediaUrl(item.logo)}
              alt={item.name}
              className="w-full h-full object-contain"
            />
          ) : (
            <span className="text-xs text-gray-400 text-center">Chưa có</span>
          )}
        </div>
      ),
    },
    {
      key: "name",
      label: "Tên nhà tài trợ",
      render: (item) => <p className="font-medium min-w-48">{item.name}</p>,
    },
    {
      key: "businessField",
      label: "Ngành nghề",
      render: (item) => (
        <p className="min-w-56 max-w-sm whitespace-pre-line">
          {item.businessField || "-"}
        </p>
      ),
    },
    {
      key: "contact",
      label: "Liên hệ",
      render: (item) => (
        <div className="min-w-52 text-sm space-y-1">
          {item.phoneNumber && <p>ĐT: {item.phoneNumber}</p>}
          {item.email && <p>Email: {item.email}</p>}
          {item.website && (
            <a
              href={item.website}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              {item.website}
            </a>
          )}
          {!item.phoneNumber && !item.email && !item.website && <p>-</p>}
        </div>
      ),
    },
    {
      key: "address",
      label: "Địa chỉ",
      render: (item) => <p className="min-w-56 max-w-md">{item.address || "-"}</p>,
    },
    {
      key: "priority",
      label: "Thứ tự",
      render: (item) => <p className="text-center">{item.priority ?? 0}</p>,
    },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
            item.status ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
          }`}
        >
          {item.status ? "Hiển thị" : "Ẩn"}
        </span>
      ),
    },
  ];

  if (
    hasPermission("SPONSOR_UPDATE") ||
    hasPermission("SPONSOR_DELETE") ||
    canViewAuditHistory()
  ) {
    headers.push({ key: "setting", label: "Thao tác" });
  }

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("SPONSOR_UPDATE") && (
          <button
            onClick={() => handleOpenModal(item)}
            title="Chỉnh sửa"
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>
        )}
        {hasPermission("SPONSOR_DELETE") && (
          <button
            onClick={() => handleOpenDeleteModal(item.id)}
            title="Xóa"
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
        <AuditHistoryButton
          module="SPONSOR"
          entityType="Sponsor"
          entityId={item.id}
        />
      </div>
    ),
  };

  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="p-4 text-red-500 bg-red-50 rounded-md">
        Đã xảy ra lỗi: {error?.messages || error?.message || "Không thể tải dữ liệu"}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Quản lý nhà tài trợ</h1>
          <p className="text-sm text-gray-500 mt-1">
            Quản lý logo, thông tin liên hệ và bài viết chi tiết của từng nhà tài trợ.
          </p>
        </div>
        <button
          onClick={() => dispatch(fetchSponsors())}
          className="btn btn-outline btn-sm w-fit"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Tải lại
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
        {hasPermission("SPONSOR_CREATE") ? (
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-success transition-colors px-4 py-2 rounded w-fit"
          >
            + Thêm nhà tài trợ
          </button>
        ) : (
          <div />
        )}
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Tìm theo tên, ngành nghề, sđt, email..."
          className="input input-bordered w-full md:max-w-sm"
        />
      </div>

      <div className="rounded-lg shadow bg-base-100">
        <TableComponent
          headers={headers}
          data={filteredSponsors}
          settings={tableSettings}
        />
      </div>

      {isModalOpen && (
        <SponsorModal sponsor={currentSponsor} onClose={handleCloseModal} />
      )}

      <ConfirmDeleteModal
        isOpen={isDeleteOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Sponsors;




